import React, { useEffect, useMemo, useState } from 'react';
import { Btn, Chip, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, TextInput, fmtDate, panelPost, useAdminGet } from '../kit';
import { Fact, Pager, Pill, type Tone, num, pageInfo, rowsOf, str, useDebounced } from '../ipintel/shared';
import { ExcelButton, LISTING_EXPORT_PATH, TEAM_INBOX, useEscape } from './shared';

/**
 * Listing requests — the "Get your university listed" form on /universities
 * (public POST /api/v1/universities/listing-request). Each request is e-mailed
 * to info@universitytech.io with the requester as Reply-To, so the
 * conversation happens in the inbox; this is the register: who asked,
 * what happened next, and a note for the team.
 * GET universities/listing_requests {q, status, from, to, page, per_page} ·
 * POST universities/listing_request_update {id, status?, admin_note?} ·
 * POST universities/listing_request_resend {id} · Excel via LISTING_EXPORT_PATH.
 */

export const LISTING_STATUSES = ['new', 'contacted', 'listed', 'declined'] as const;
const META: Record<string, { label: string; tone: Tone; hint: string }> = {
  new: { label: 'New', tone: 'warn', hint: 'Nobody has answered yet' },
  contacted: { label: 'Contacted', tone: 'info', hint: 'The team has replied / is talking to them' },
  listed: { label: 'Listed', tone: 'good', hint: 'Their technologies are on the site' },
  declined: { label: 'Declined', tone: 'neutral', hint: 'Not going ahead' },
};

const StatusPill: React.FC<{ status: any }> = ({ status }) => {
  const m = META[str(status)];
  return <Pill tone={m?.tone || 'neutral'} title={m?.hint}>{m?.label || str(status) || '—'}</Pill>;
};

const MailPill: React.FC<{ status: any; error?: any }> = ({ status, error }) => {
  const s = str(status);
  if (s === 'sent') return <Pill tone="good" title={`Sent to ${TEAM_INBOX}, Reply-To the requester`}>Sent</Pill>;
  if (s === 'failed') return <Pill tone="bad" title={str(error) || 'The e-mail did not go out'}>Failed</Pill>;
  if (s === 'pending' || s === 'queued') return <Pill tone="neutral">Sending…</Pill>;
  return <Pill tone="neutral" title={str(error)}>{s || '—'}</Pill>;
};

const TECH_RANGES: Record<string, string> = {
  '<10': 'Under 10', '10-50': '10–50', '50-200': '50–200', '200-1000': '200–1,000', '1000+': '1,000+',
};

const replyHref = (r: any): string => {
  const quoted = str(r.message).slice(0, 1200).split(/\r?\n/).map(l => `> ${l}`).join('\n');
  const body = `Hi ${str(r.contact_name).split(' ')[0] || 'there'},\n\nThank you for asking to list ${str(r.institution) || 'your university'} on UniversityTech.io.\n\n\n`
    + (r.message ? `You wrote:\n${quoted}\n` : '');
  const subject = `Listing ${str(r.institution) || 'your university'} on UniversityTech.io${r.ref ? ` (${r.ref})` : ''}`;
  return `mailto:${encodeURIComponent(str(r.email))}?cc=${encodeURIComponent(TEAM_INBOX)}`
    + `&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

const extLink = (u: any) => {
  const v = str(u);
  if (!v) return '';
  const href = /^https?:\/\//i.test(v) ? v : `https://${v}`;
  return <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary-700 underline break-all">{v.replace(/^https?:\/\/(www\.)?/i, '')}</a>;
};

// ---------------------------------------------------------------------------
const RequestDrawer: React.FC<{ row: any; onClose: () => void; onChanged: () => void }> = ({ row, onClose, onChanged }) => {
  useEscape(onClose);
  const [r, setR] = useState<any>(row);
  const [status, setStatus] = useState(str(row.status) || 'new');
  const [note, setNote] = useState(str(row.admin_note));
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 6000);
    return () => clearTimeout(t);
  }, [armed]);

  const apply = (next: any) => {
    if (!next || typeof next !== 'object') return;
    setR((cur: any) => ({ ...cur, ...next }));
    if (next.status !== undefined) setStatus(str(next.status) || 'new');
    if (next.admin_note !== undefined) setNote(str(next.admin_note));
  };
  const save = async (body: { status?: string; admin_note?: string }, ok: string) => {
    setBusy('save'); setMsg(null);
    const { data, error } = await panelPost('universities', 'listing_request_update', { id: r.id, ...body });
    setBusy('');
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    apply(data?.row ?? data);
    setMsg({ kind: 'ok', text: ok });
    onChanged();
  };
  const resend = async () => {
    if (!armed) { setArmed(true); return; }
    setArmed(false); setBusy('resend'); setMsg(null);
    const { data, error } = await panelPost('universities', 'listing_request_resend', { id: r.id });
    setBusy('');
    if (error) { setMsg({ kind: 'error', text: `Resend failed: ${error}` }); return; }
    const next = data?.row ?? data;
    apply(next);
    onChanged();
    setMsg(data?.sent === false || str(next?.email_status) === 'failed'
      ? { kind: 'error', text: `The e-mail did NOT go out: ${str(next?.email_error) || 'see Email › Logs'}.` }
      : { kind: 'ok', text: `Re-sent to ${TEAM_INBOX} (Reply-To ${str(r.email)}).` });
  };
  const dirty = status !== (str(r.status) || 'new') || note !== str(r.admin_note);

  return (
    <div className="fixed inset-0 z-40 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative h-full bg-white shadow-xl flex flex-col" style={{ width: 'min(96vw, 44rem)' }}
        onClick={e => e.stopPropagation()} role="dialog" aria-label="University listing request">
        <div className="px-5 py-4 border-b border-grayscale-300 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-base font-bold text-grayscale-1300 font-mono">{r.ref || `#${r.id}`}</span>
              <StatusPill status={r.status} />
              <MailPill status={r.email_status} error={r.email_error} />
            </div>
            <div className="text-sm font-medium text-grayscale-1300 truncate">{str(r.institution) || '—'}</div>
            <div className="text-xs text-grayscale-1100 truncate">{[str(r.contact_name), str(r.email)].filter(Boolean).join(' · ')}</div>
          </div>
          <Btn small onClick={onClose}>Close</Btn>
        </div>
        <div className="p-5 overflow-y-auto flex flex-col gap-4">
          {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
          <Section title="Their request" right={<span className="text-xs text-grayscale-1100">{r.created_at ? fmtDate(r.created_at) : ''}</span>}>
            {r.message ? (
              <blockquote className="border-l-4 border-primary-400 bg-primary-100/40 rounded-r-lg px-4 py-3 text-sm text-grayscale-1300 whitespace-pre-wrap break-words">
                {str(r.message)}
              </blockquote>
            ) : <div className="text-xs text-grayscale-1100">No message.</div>}
            <div className="grid grid-cols-2 gap-2">
              <Fact label="Institution" value={r.institution} />
              <Fact label="Website" value={extLink(r.website)} />
              <Fact label="Country" value={[str(r.country), r.country_geo && str(r.country_geo) !== str(r.country) ? `(IP: ${str(r.country_geo)})` : ''].filter(Boolean).join(' ')} />
              <Fact label="Technologies" value={TECH_RANGES[str(r.tech_count_range)] || str(r.tech_count_range)} />
              <Fact label="Contact" value={[str(r.contact_name), str(r.role)].filter(Boolean).join(' — ')} />
              <Fact label="Email" value={r.email ? <a href={`mailto:${r.email}`} className="text-primary-700 underline">{r.email}</a> : ''} />
              <Fact label="Phone" value={r.phone} />
              <Fact label="TTO portal" value={extLink(r.tto_portal_url)} />
              <Fact label="Account" value={r.user_id ? <span className="font-mono text-xs">{r.user_id}</span> : 'Not signed in'} />
              <Fact label="Sent from" value={r.page_url ? <span className="text-xs break-all">{str(r.page_url).replace(/^https?:\/\/[^/]+/i, '') || '/'}</span> : ''} />
            </div>
          </Section>

          <Section title="Reply">
            <div className="flex items-center gap-2 flex-wrap">
              <a href={replyHref(r)} className="py-1.5 px-3.5 text-sm rounded-full font-grotesk font-medium whitespace-nowrap bg-primary-500 text-grayscale-100">
                Reply by e-mail
              </a>
              {str(r.status) === 'new' && (
                <Btn small onClick={() => save({ status: 'contacted' }, 'Marked contacted.')} disabled={!!busy}>Mark contacted</Btn>
              )}
            </div>
            <div className="text-[11px] text-grayscale-1100">
              Opens your mail app addressed to <b>{str(r.email)}</b> with <b>{TEAM_INBOX}</b> in CC. The original e-mail
              in the {TEAM_INBOX} inbox has them as Reply-To, so replying there works too.
            </div>
          </Section>

          <Section title="Status & note">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-grayscale-1100">Status</span>
              <Select value={status} onChange={setStatus} options={[...LISTING_STATUSES]}
                labels={Object.fromEntries(LISTING_STATUSES.map(s => [s, META[s].label]))} />
              <span className="text-[11px] text-grayscale-1100">{META[status]?.hint}</span>
            </div>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
              placeholder="Internal note — never sent to the requester"
              className="w-full px-3 py-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm" />
            <div className="flex items-center gap-2 flex-wrap">
              <Btn kind="primary" small disabled={!dirty || !!busy} onClick={() => save({ status, admin_note: note }, 'Saved.')}>
                {busy === 'save' ? 'Saving…' : 'Save status & note'}
              </Btn>
              {dirty && <span className="text-[11px] text-amber-700">unsaved changes</span>}
              {r.updated_at && (
                <span className="text-[11px] text-grayscale-1100">last change {fmtDate(r.updated_at)}{r.updated_by ? ` by ${r.updated_by}` : ''}</span>
              )}
            </div>
          </Section>

          <Section title="Re-send the e-mail">
            {str(r.email_status) === 'failed' && r.email_error && <Msg kind="error">Last attempt failed: {str(r.email_error)}</Msg>}
            <div className="flex items-center gap-2 flex-wrap">
              <Btn small kind={armed ? 'primary' : 'subtle'} onClick={resend} disabled={!!busy}>
                {busy === 'resend' ? 'Sending…' : armed ? `Confirm — e-mail ${TEAM_INBOX}` : 'Re-send e-mail'}
              </Btn>
              <span className="text-[11px] text-grayscale-1100">
                Sends the request to {TEAM_INBOX} again (Reply-To the requester; they are not copied).
              </span>
            </div>
          </Section>
          {r.user_agent && <div className="text-[10px] text-grayscale-1100 break-all">Browser: {str(r.user_agent)}</div>}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
const COLS = ['created_at', 'ref', 'institution', 'country', 'contact_name', 'email', 'tech_count_range', 'status', 'email_status'];
const LABELS: Record<string, string> = {
  created_at: 'When', ref: 'Ref', institution: 'Institution', country: 'Country', contact_name: 'Contact',
  email: 'Email', tech_count_range: 'Technologies', status: 'Status', email_status: 'E-mail',
};
type Filters = { q: string; status: string; from: string; to: string };
const EMPTY: Filters = { q: '', status: '', from: '', to: '' };

const ListingRequests: React.FC = () => {
  const [f, setF] = useState<Filters>(EMPTY);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState('50');
  const [tick, setTick] = useState(0);
  const [dlErr, setDlErr] = useState('');
  const [open, setOpen] = useState<any | null>(null);
  const q = useDebounced(f.q);
  const set = (k: keyof Filters) => (v: string) => setF(s => ({ ...s, [k]: v }));

  const filterParams = useMemo(() => ({ q, status: f.status, from: f.from, to: f.to }), [q, f.status, f.from, f.to]);
  const filterKey = JSON.stringify(filterParams);
  useEffect(() => { setPage(0); }, [filterKey, perPage]);

  const res = useAdminGet('universities', 'listing_requests', { ...filterParams, page, per_page: perPage }, [filterKey, page, perPage, tick]);
  const d = res.data || {};
  const rows = rowsOf(d, 'requests');
  const per = Number(perPage) || 50;
  const { total, pages } = pageInfo(d, rows, per);
  const counts = d.counts || {};
  const all = LISTING_STATUSES.reduce((a, s) => a + num(counts[s]), 0);

  const chips: { label: string; value: string; clear: () => void }[] = [];
  if (f.q) chips.push({ label: 'Search', value: f.q, clear: () => set('q')('') });
  if (f.status) chips.push({ label: 'Status', value: META[f.status]?.label || f.status, clear: () => set('status')('') });
  if (f.from) chips.push({ label: 'From', value: f.from, clear: () => set('from')('') });
  if (f.to) chips.push({ label: 'To', value: f.to, clear: () => set('to')('') });

  return (
    <div className="flex flex-col gap-4">
      {d.counts && (
        <KPIRow>
          {LISTING_STATUSES.map(s => (
            <KPI key={s} label={META[s].label} value={num(counts[s]).toLocaleString()} hint={META[s].hint}
              onClick={() => setF(x => ({ ...x, status: x.status === s ? '' : s }))} active={f.status === s} />
          ))}
          <KPI label="All requests" value={all.toLocaleString()} hint="all time" onClick={() => set('status')('')} active={!f.status && !chips.length} />
        </KPIRow>
      )}
      <Section title="University listing requests"
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-grayscale-1100">{total.toLocaleString()} request{total === 1 ? '' : 's'}</span>
            {chips.length > 0 && <Btn small onClick={() => setF(EMPTY)}>Clear {chips.length} filter{chips.length === 1 ? '' : 's'}</Btn>}
            <Btn small onClick={() => setTick(t => t + 1)} disabled={res.loading}>Refresh</Btn>
            <ExcelButton path={LISTING_EXPORT_PATH} params={filterParams} onError={setDlErr}
              fallback="university-listing-requests" what="These requests" />
          </div>
        }>
        <p className="text-xs text-grayscale-1100 -mt-1">
          From the “Get your university listed” form on /universities. Each one is e-mailed to {TEAM_INBOX} with the
          requester as Reply-To — the wording is under the <b>Emails</b> tab.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <TextInput value={f.q} onChange={set('q')} wide placeholder="Search institution, name, email, ref…" />
          <Select value={f.status} onChange={set('status')} options={['', ...LISTING_STATUSES]}
            labels={{ '': 'Any status', ...Object.fromEntries(LISTING_STATUSES.map(s => [s, `${META[s].label}${counts[s] !== undefined ? ` (${num(counts[s])})` : ''}`])) }} />
          <span className="text-xs text-grayscale-1100">from</span>
          <TextInput type="date" value={f.from} onChange={set('from')} />
          <span className="text-xs text-grayscale-1100">to</span>
          <TextInput type="date" value={f.to} onChange={set('to')} />
        </div>
        {chips.length > 0 && <div className="flex gap-1.5 flex-wrap">{chips.map(c => <Chip key={c.label} label={c.label} value={c.value} onClear={c.clear} />)}</div>}
        {dlErr && <Msg kind="error">Export failed: {dlErr}</Msg>}
        {res.error && <Msg kind="error">{res.error}</Msg>}
        {res.loading && !res.data ? <Loading /> : (
          <DataTable rows={rows} columns={COLS} labels={LABELS} maxHeight="40rem"
            empty={chips.length ? 'No requests match these filters.' : 'No listing requests yet.'}
            render={{
              created_at: r => <span className="text-xs" title={str(r.created_at)}>{fmtDate(r.created_at)}</span>,
              ref: r => (
                <button type="button" onClick={() => setOpen(r)} title="Open this request"
                  className="font-mono text-xs font-bold text-grayscale-1300 underline underline-offset-2">{r.ref || `#${r.id}`}</button>
              ),
              institution: r => <span className="font-medium block max-w-[16rem] truncate" title={str(r.institution)}>{str(r.institution) || '—'}</span>,
              email: r => (r.email ? <a href={`mailto:${r.email}`} className="text-primary-700 underline">{r.email}</a> : '—'),
              tech_count_range: r => TECH_RANGES[str(r.tech_count_range)] || str(r.tech_count_range) || '—',
              status: r => <StatusPill status={r.status} />,
              email_status: r => <MailPill status={r.email_status} error={r.email_error} />,
            }}
            actions={r => <Btn small onClick={() => setOpen(r)}>Open</Btn>} />
        )}
        {pages > 1 && (
          <Pager page={page} pages={pages} total={total} shown={rows.length} per={per} onPage={setPage} perPage={perPage} onPerPage={setPerPage} />
        )}
      </Section>
      {open && <RequestDrawer key={str(open.id)} row={open} onClose={() => setOpen(null)} onChanged={() => setTick(t => t + 1)} />}
    </div>
  );
};

export default ListingRequests;
